<template>
  <div class="page-container">
    <ModalBox
      :visible="showModal"
      :infoTitle="infoTitle"
      :infoMessage="infoMessage"
      @close="showModal = false"
    />

    <div class="tab-content">
      <h4>Advanced Capabilities</h4>
      <p class="page-subtitle">Sensitive operations for advanced workflows.</p>

      <div class="section-card">
        <button class="section-header" @click="toggleSection('amberActivation')">
          <span>One Step Amber Activation</span>
          <span>{{ openSection === 'amberActivation' ? '▲' : '▼' }}</span>
        </button>

        <div v-if="openSection === 'amberActivation'" class="section-body">
          <div class="options">
            <button
              class="button-option"
              :class="{ active: selectedMethod === 'byId' }"
              @click="selectMethod('byId')"
            >
              Search by Plant ID
            </button>
            <button
              class="button-option"
              :class="{ active: selectedMethod === 'byAutocomplete' }"
              @click="selectMethod('byAutocomplete')"
            >
              Search by Name
            </button>
          </div>

          <div v-if="selectedMethod === 'byId'" class="search-block">
            <label>Enter Renox Plant ID</label>
            <div class="row">
              <input
                class="custom-input"
                type="text"
                v-model="memberId"
                placeholder="Enter Plant ID"
              />
              <button
                class="custom-button"
                :class="{ active: memberId !== '' && Number(memberId) > 0 }"
                @click="getPlantById"
              >
                Search
              </button>
            </div>
          </div>

          <div v-if="selectedMethod === 'byAutocomplete'" class="search-block">
            <label>Search by account and plant name</label>
            <input
              class="custom-input full-width"
              type="text"
              v-model="searchTermAutocomplete"
              placeholder="Type at least 3 characters"
              @input="getPlantByAutocomplete(searchTermAutocomplete)"
            />
          </div>

          <h6>Found Plants ({{ listPlantByIdDataResponse.length }})</h6>
          <div class="result-box" v-if="listPlantByIdDataResponse.length > 0">
            <div v-for="(item, index) in listPlantByIdDataResponse" :key="index">
              <a href="#" @click.prevent="selectedResultIndex = index">
                <div class="result-item" :class="{ selectedResult: selectedResultIndex === index }">
                  <p>Plant ID: {{ item.plant_id }}</p>
                  <p>Plant Name: {{ item.plant_name }}</p>
                  <p>Account Name: {{ item.account_name }}</p>
                  <p>Date Created: {{ item.date_created }}</p>
                </div>
              </a>
            </div>
          </div>

          <div class="search-block">
            <label>Amber Token</label>
            <input
              class="custom-input full-width"
              type="text"
              v-model="amberToken"
              placeholder="Enter Amber token"
            />
          </div>

          <button
            class="custom-button submit-button"
            :class="{ active: canActivate }"
            @click="setAmberActivation"
            :disabled="!canActivate || isSubmitting"
          >
            {{ isSubmitting ? 'Submitting...' : 'Activate Amber' }}
          </button>
        </div>
      </div>

      <div class="section-card">
        <button class="section-header" @click="toggleSection('poolUserChanges')">
          <span>AI Pool User Changes</span>
          <span>{{ openSection === 'poolUserChanges' ? '▲' : '▼' }}</span>
        </button>

        <div v-if="openSection === 'poolUserChanges'" class="section-body">
          <AiPoolUserChangesSection />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDevModeState } from '@/services/cookieService';
import { eventBus } from '@/services/eventBus';
import { apiRequest } from '@/services/apiService';
import ModalBox from '@/components/cards/ModalBox.vue';
import AiPoolUserChangesSection from '@/components/cards/AiPoolUserChangesSection.vue';

export default {
  name: 'AdvancedCapabilitiesPage',
  components: {
    ModalBox,
    AiPoolUserChangesSection,
  },
  data() {
    return {
      subDataUrl: '/api/v1.1/data',
      openSection: null,
      selectedMethod: 'byAutocomplete',
      memberId: '',
      searchTermAutocomplete: '',
      listPlantByIdDataResponse: [],
      selectedResultIndex: -1,
      amberToken: '',
      isSubmitting: false,
      showModal: false,
      infoTitle: '',
      infoMessage: '',
    };
  },
  computed: {
    selectedPlant() {
      if (this.selectedResultIndex < 0) {
        return null;
      }
      return this.listPlantByIdDataResponse[this.selectedResultIndex] || null;
    },
    canActivate() {
      return !!(this.selectedPlant && this.amberToken.trim());
    },
  },
  methods: {
    toggleSection(sectionName) {
      this.openSection = this.openSection === sectionName ? null : sectionName;
    },
    handleEnvironmentChange(isDevMode) {
      this.subDataUrl = isDevMode ? '/dev-api/api/v1.1/data' : '/api/v1.1/data';
    },
    initializeEnvironment() {
      const devModeState = getDevModeState();
      if (devModeState.isDevMode !== undefined) {
        this.subDataUrl = devModeState.isDevMode ? '/dev-api/api/v1.1/data' : '/api/v1.1/data';
      }
    },
    selectMethod(method) {
      this.selectedMethod = method;
      this.memberId = '';
      this.searchTermAutocomplete = '';
      this.selectedResultIndex = -1;
      this.listPlantByIdDataResponse = [];
    },
    async fetchPlantInfo(plantId) {
      const url = `${this.subDataUrl}/bridge/rx_data/plant/plant_info/get`;
      const response = await apiRequest('post', url, { plant_id: Number(plantId) });
      if (response.errorCode === 0 && response.data) {
        return response.data;
      }
      return [];
    },
    async getPlantById() {
      this.selectedResultIndex = -1;
      if (!Number(this.memberId)) {
        return;
      }

      try {
        const plantData = await this.fetchPlantInfo(this.memberId);
        this.listPlantByIdDataResponse = plantData || [];
      } catch (error) {
        this.infoTitle = 'Error';
        this.infoMessage = 'Failed to fetch plant by ID. Please try again later.';
        this.showModal = true;
      }
    },
    async getPlantByAutocomplete(inputValue) {
      this.selectedResultIndex = -1;
      if (inputValue.trim() === '' || inputValue.length < 3) {
        this.listPlantByIdDataResponse = [];
        return;
      }

      try {
        const url = `${this.subDataUrl}/bridge/rx_data/autocomplete/plant_info/get`;
        const response = await apiRequest('post', url, { input_text: inputValue });
        if (response.errorCode === 0 && response.data) {
          this.listPlantByIdDataResponse = response.data;
        }
      } catch (error) {
        this.infoTitle = 'Error';
        this.infoMessage = 'Failed to fetch plant suggestions. Please try again later.';
        this.showModal = true;
      }
    },
    async setAmberActivation() {
      if (!this.canActivate) {
        this.infoTitle = 'Missing Information';
        this.infoMessage = 'Please select a plant and enter an Amber token.';
        this.showModal = true;
        return;
      }

      this.isSubmitting = true;
      try {
        const url = `${this.subDataUrl}/amber_activation/set`;
        const payload = {
          plant_id: this.selectedPlant.plant_id,
          token: this.amberToken.trim(),
        };
        const response = await apiRequest('post', url, payload);

        if (response.errorCode === 0) {
          this.infoTitle = 'Success';
          this.infoMessage = 'Amber activation token set successfully.';
          this.amberToken = '';
          this.selectedResultIndex = -1;
          this.listPlantByIdDataResponse = [];
          this.memberId = '';
          this.searchTermAutocomplete = '';
        } else {
          this.infoTitle = 'Error';
          this.infoMessage = response.infoText || response.errorMsg || 'Unexpected error occurred';
        }
        this.showModal = true;
      } catch (error) {
        this.infoTitle = 'Error';
        this.infoMessage = 'Unexpected backend response. Please try again later.';
        this.showModal = true;
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  created() {
    this.initializeEnvironment();
    eventBus.on('environment-changed', this.handleEnvironmentChange);
  },
  beforeUnmount() {
    eventBus.off('environment-changed', this.handleEnvironmentChange);
  },
};
</script>

<style scoped>
.page-container {
  margin: 0;
  padding: 0;
}

.tab-content {
  margin: 20px;
  padding: 20px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-primary);
}

.page-subtitle {
  color: var(--text-tertiary);
}

.section-card {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 20px;
}

.section-header {
  width: 100%;
  border: none;
  padding: 14px 16px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  cursor: pointer;
}

.section-body {
  padding: 16px;
  background: var(--card-bg);
}

.options {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.button-option {
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.8rem;
  padding: 10px 16px;
  transition: all 0.2s ease;
}

.button-option.active {
  border: 2px solid #20cc20;
  background: #20cc20;
  color: #ffffff;
  font-weight: 600;
}

.search-block {
  margin: 10px 0 16px;
}

.row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.custom-input {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-primary);
  padding: 10px 12px;
}

.full-width {
  width: 100%;
}

.custom-button {
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  font-size: 14px;
  padding: 10px 14px;
}

.custom-button.active {
  border-color: #20cc20;
}

.custom-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.submit-button {
  margin-top: 6px;
}

.result-box {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  max-height: 250px;
  overflow-y: auto;
  padding: 8px;
  margin-bottom: 14px;
}

.result-item {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.selectedResult {
  background: var(--custom-color1);
  color: #ffffff;
}

a {
  text-decoration: none;
}

@media (max-width: 750px) {
  .options,
  .row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
